import { ArrowUpRight16 } from '@carbon/icons-react';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CustomerPortalPage } from 'api/customer-portal/types';
import { Menu } from 'components/atoms/dropdown';
import { Popover } from 'components/atoms/popover';
import { HelperText01 } from 'ozone/atoms';

interface Props {
  pages: CustomerPortalPage[];
  onCopy?: (page: CustomerPortalPage) => void;
  onMove?: (page: CustomerPortalPage) => void;
  onDuplicate?: () => void;
  onPageClick: (page: CustomerPortalPage) => void;
  currentPageRefId: string;
  getTooltipContainer?: () => HTMLElement;
}

type Mode = 'copy' | 'move' | undefined;

const MenuItemInnerWrapper = styled.div`
  display: inline-flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuItemActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`;

const Copied = styled.span`
  color: var(--green-50);
`;

const GotoPage = styled.span`
  display: inline-flex;
  justify-content: center;
  width: 40px;
  align-items: center;
  height: 100%;
  svg {
    fill: var(--blue-50);
  }
`;

const StyledMenu = styled(Menu)`
  border-radius: var(--border-radius-default);
  max-height: 300px;
  overflow-y: auto;
`;

const StyledMenuItem = styled(Menu.Item)`
  margin-top: 0;
  margin-bottom: 0 !important;
  padding: 0 var(--spacing-05);
  height: 32px;
  &:hover {
    background: var(--hover-color);
    color: var(--text-color);
  }
`;

const StyledMenuHeader = styled(Menu.Header)`
  height: 20px;
  align-items: center;
`;

const StyledHelperText01 = styled(HelperText01)`
  /* TODO: use vendor color */
  color: var(--cp-black-opacity-60-hover);
`;

export const CopyOrMoveSectionsMenu: React.FC<Props> = ({
  pages,
  onCopy,
  onMove,
  children,
  currentPageRefId,
  onPageClick,
  getTooltipContainer,
  onDuplicate,
}) => {
  const [isVisible, setVisible] = useState(false);
  const [mode, setMode] = useState<Mode>();
  const [selectedPage, setSelectedPage] =
    useState<CustomerPortalPage | null>(null);

  return (
    <Popover
      getTooltipContainer={getTooltipContainer}
      trigger={['click']}
      placement="bottomLeft"
      destroyTooltipOnHide
      visible={isVisible}
      onVisibleChange={(visible) => {
        setVisible(visible);
        if (!visible) {
          setMode(undefined);
          setSelectedPage(null);
        }
      }}
      content={
        <StyledMenu>
          {!mode ? (
            <>
              <StyledMenuItem onClick={() => setMode('copy')}>
                <FormattedMessage id="customer_portal.builder.copy_section_to" />
              </StyledMenuItem>
              <StyledMenuItem onClick={() => setMode('move')}>
                <FormattedMessage id="customer_portal.builder.move_section_to" />
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  setMode(undefined);
                  setVisible(false);
                  onDuplicate?.();
                }}
              >
                <FormattedMessage id="customer_portal.builder.duplicate_section_to" />
              </StyledMenuItem>
            </>
          ) : (
            <>
              {mode ? (
                <StyledMenuHeader>
                  <FormattedMessage
                    id={`customer_portal.builder.${mode}_section_to`}
                  />
                </StyledMenuHeader>
              ) : (
                <> </>
              )}
              {pages.map((page) => (
                <StyledMenuItem
                  key={page.pageReferenceId}
                  onClick={() => {
                    if (page?.pageReferenceId !== currentPageRefId) {
                      if (mode === 'copy') {
                        onCopy?.(page);
                      } else if (mode === 'move') {
                        onMove?.(page);
                      }
                      setSelectedPage(page);
                    }
                  }}
                >
                  <MenuItemInnerWrapper>
                    <span>{page.pageName}</span>
                    <MenuItemActionsWrapper>
                      {currentPageRefId === page.pageReferenceId ? (
                        <StyledHelperText01>
                          <FormattedMessage id="customer_portal.builder.copy_or_move_section.current_page" />
                        </StyledHelperText01>
                      ) : selectedPage?.pageReferenceId ===
                        page.pageReferenceId ? (
                        <>
                          <GotoPage
                            onClick={(event) => {
                              event.stopPropagation();
                              onPageClick(page);
                            }}
                          >
                            <ArrowUpRight16 />
                          </GotoPage>
                          <Copied>
                            <FormattedMessage id="copied" />
                          </Copied>
                        </>
                      ) : (
                        <></>
                      )}
                    </MenuItemActionsWrapper>
                  </MenuItemInnerWrapper>
                </StyledMenuItem>
              ))}
            </>
          )}
        </StyledMenu>
      }
    >
      <span>{children}</span>
    </Popover>
  );
};
